// Catálogo único de frases da auditoria. Recebe o evento bruto e devolve texto para leigos.
// Nunca devolve JSON, "_" ou código técnico.

export type EventoAuditoria = {
  id: number;
  ocorrido_em: string;
  ator_nome: string;
  ator_papel: string;
  origem: string;
  tipo: string;
  entidade: string;
  entidade_id: string | null;
  entidade_rotulo: string | null;
  alteracoes: { campo: string; de: unknown; para: unknown }[] | null;
  contexto: Record<string, any> | null;
  ocorrencia?: number;
  correlacao_id?: number;
};

export const CATEGORIAS: Record<string, { label: string; entidades: string[]; tipos?: string[] }> = {
  laudos: {
    label: "Laudos",
    entidades: ["laudo", "peca_do_laudo", "resposta_do_laudo", "lote", "lote_laudo", "ficha_tecnica", "solicitacao_laudo"],
  },
  creditos: {
    label: "Créditos e compras",
    entidades: ["compra_credito", "movimento_credito", "reserva_credito", "saldo_credito", "reembolso", "chargeback", "repasse", "lote_credito"],
  },
  repasses: {
    label: "Repasses Asaas",
    entidades: [
      "lancamento_repasse", "lote_repasse", "subconta_asaas", "transferencia_asaas",
      "compensacao_asaas", "cotacao_asaas", "pagamento_cotacao_asaas", "taxa_asaas", "webhook_asaas",
    ],
  },
  faturas: {
    label: "Faturas e financeiro",
    entidades: ["fatura", "item_fatura", "fatura_parceiro", "item_fatura_parceiro", "lancamento", "nota_fiscal"],
  },
  cadastros: {
    label: "Cadastros",
    entidades: ["empresa", "usuario", "convite_usuario", "codigo_convite", "veiculo", "veiculo_foto", "peca", "pergunta", "art", "certificado_rt"],
  },
  // Task 5: uma categoria casa por entidade OU tipo (laudo.pdf_* aparece em laudos e entregas de propósito).
  acessos: { label: "Acessos", entidades: [], tipos: ["auth.login", "auth.senha_alterada", "usuario.senha_redefinida", "auditoria.exportada"] },
  entregas: { label: "PDFs e entregas", entidades: ["email"], tipos: ["laudo.pdf_gerado", "laudo.pdf_baixado", "laudo.assinado"] },
  configuracoes: {
    label: "Configurações",
    entidades: ["configuracao_vistoriadora", "configuracao_financeira", "preco_vistoriadora", "preco_plataforma", "conexao_bling", "restricao_vistoriadora"],
  },
};

// Rótulos de valores de enums reais (shared/schema.ts) e status usados nas tabelas auditadas.
const ROTULOS: Record<string, string> = {
  auto_leve: "Auto / Leve", moto: "Moto", caminhao: "Caminhão", onibus: "Ônibus",
  rascunho: "Rascunho", enviado_analise: "Enviado para análise", em_vistoria: "Em Vistoria",
  em_avaliacao_rt: "Em avaliação do RT", assinado: "Assinado", finalizado: "Finalizado",
  pendente: "Pendente", pago: "Pago", vencido: "Vencido", cancelado: "Cancelado", cancelada: "Cancelada",
  atrasada: "Atrasada", bloqueada: "Bloqueada", aprovado: "Aprovado", emitida: "Emitida", erro: "Erro",
  ativa: "Ativa", encerrada: "Encerrada",
  venda_imediata: "Venda imediata", recuperacao: "Recuperação", inexistente: "Inexistente", descarte: "Descarte",
};

const CAMPOS: Record<string, string> = {
  aguardando_credito_desde: "Aguardando crédito desde",
  status: "Status",
  cartela: "Cartela de etiquetas",
  cartela_confirmada: "Confirmação da cartela",
  pdf_url: "PDF",
  data_assinatura: "Data da assinatura",
  ativo: "Situação (ativo)",
  valor_total: "Valor total",
  vencimento: "Vencimento",
  nome: "Nome",
  email: "E-mail",
  telefone: "Telefone",
};

const ENTIDADES: Record<string, string> = {
  laudo: "Laudo",
  empresa: "Empresa",
  usuario: "Usuário",
  fatura: "Fatura",
  fatura_parceiro: "Fatura",
  compra_credito: "Compra de crédito",
  movimento_credito: "Movimento de crédito",
  veiculo: "Veículo",
  veiculo_foto: "Foto do veículo",
  peca: "Peça",
  email: "E-mail",
  nota_fiscal: "Nota fiscal",
  repasse: "Repasse",
  taxa_asaas: "Taxas Asaas",
  cotacao_asaas: "Cotação",
  subconta_asaas: "Subconta Asaas",
  webhook_asaas: "Evento do Asaas",
  compensacao_asaas: "Compensação de tarifa",
  pagamento_cotacao_asaas: "Pagamento da cotação",
  transferencia_asaas: "Transferência Asaas",
  lote_credito: "Lote de créditos",
  lancamento_repasse: "Lançamento de repasse",
  lote_repasse: "Lote de repasse",
};

const DINHEIRO = /^(valor|preco|multa|juros|subtotal|total|desconto|saldo)/;
const OCULTO = "(oculto)"; // valor sensível: os triggers nunca gravam o conteúdo

export function humanizar(s: string): string {
  return String(s)
    .replace(/_/g, " ")
    .trim()
    .replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

const brl = (n: number) =>
  "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const texto = (v: unknown, fallback: string): string =>
  typeof v === "string" && v.trim() ? v.trim().replace(/_/g, " ") : fallback;
const numero = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

function valor(campo: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "sem valor";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (typeof v === "number" || (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v) && DINHEIRO.test(campo))) {
    const n = numero(v);
    if (n === null) return "sem valor";
    return DINHEIRO.test(campo) ? brl(n) : String(n);
  }
  if (typeof v === "string") {
    if (ROTULOS[v]) return ROTULOS[v];
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      return isNaN(d.getTime())
        ? v.replace(/[TZ_]/g, " ").trim()
        : d.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
    }
    if (/^[a-z]+(_[a-z]+)+$/.test(v)) return humanizar(v);
    return v.replace(/_/g, " ");
  }
  return "(valor complexo)";
}

const AMBIENTES_ASAAS: Record<string, string> = { producao: "produção", sandbox: "sandbox", simulado: "simulado" };
const ambienteAsaas = (e: EventoAuditoria) => AMBIENTES_ASAAS[String(e.contexto?.ambiente)] ?? "ambiente desconhecido";
const ator = (e: EventoAuditoria) => texto(e.ator_nome, "Alguém");
const rotulo = (e: EventoAuditoria) => texto(e.entidade_rotulo, "") || ENTIDADES[e.entidade] || humanizar(e.entidade);
const nomeCampo = (c: string) => CAMPOS[c] ?? humanizar(c);
const alt = (e: EventoAuditoria, campo: string) => e.alteracoes?.find((a) => a.campo === campo);
const plural = (n: number, s: string, p: string) => `${n} ${n === 1 ? s : p}`;

function paraDe(a: { campo: string; de: unknown; para: unknown }) {
  return `de "${valor(a.campo, a.de)}" para "${valor(a.campo, a.para)}"`;
}

const ESPECIAIS: Record<string, (e: EventoAuditoria) => string | null> = {
  "laudo.status_alterado": (e) => {
    const a = alt(e, "status");
    return a ? `Status do ${rotulo(e)} alterado ${paraDe(a)} por ${ator(e)}` : null;
  },
  "laudo.cartela_alterada": (e) => {
    const a = alt(e, "cartela");
    if (!a) return null;
    const n = e.ocorrencia ? ` (${e.ocorrencia}ª alteração)` : "";
    return `Cartela de etiquetas do ${rotulo(e)} alterada ${paraDe(a)} por ${ator(e)}${n}`;
  },
  "laudo.pdf_gerado": (e) => `PDF do ${rotulo(e)} liberado por ${ator(e)}`,
  "laudo.pdf_baixado": (e) => `PDF do ${rotulo(e)} baixado por ${ator(e)}`,
  "laudo.assinado": (e) => `${rotulo(e)} assinado por ${ator(e)}`,
  "veiculo_foto.criado": (e) => `${ator(e)} enviou a ${rotulo(e)}`,
  "veiculo_foto.atualizado": (e) => `${ator(e)} trocou a ${rotulo(e)}`,
  "laudo.credito_alterado": (e) => {
    const a = alt(e, "aguardando_credito_desde");
    if (!a) return `Situação de crédito do ${rotulo(e)} alterada por ${ator(e)}`;
    return a.para ? `${rotulo(e)} ficou aguardando crédito do Parceiro` : `${rotulo(e)} liberado: crédito do Parceiro disponível`;
  },
  "auth.login": (e) => `${ator(e)} entrou no sistema`,
  "auth.senha_alterada": (e) => `${ator(e)} alterou a própria senha`,
  "usuario.senha_redefinida": (e) =>
    `Senha de ${rotulo(e)} redefinida por ${texto(e.contexto?.redefinido_por, ator(e))}`,
  "auditoria.exportada": (e) => `${ator(e)} exportou a auditoria`,
  "taxa_asaas.criado": (e) => `Nova versão das taxas do Asaas registrada por ${ator(e)}`,
  "taxa_asaas.sincronizada": (e) =>
    `Taxas do Asaas (${ambienteAsaas(e)}) sincronizadas por ${ator(e)}${e.contexto?.reutilizado ? " — sem mudanças" : " — novas taxas registradas"}`,
  "taxa_asaas.sincronizacao_falhou": (e) =>
    `Falha ao sincronizar as taxas do Asaas (${ambienteAsaas(e)}): ${texto(e.contexto?.erro, "motivo não informado").replace(/[{}\[\]]/g, "").slice(0, 160)}`,
  "taxa_asaas.fallback_usado": (e) =>
    `Taxas do Asaas (${ambienteAsaas(e)}): Asaas indisponível, usada a última versão válida`,
  "validacao_saque.aprovada": (e) =>
    `Saque aprovado pela validação do Asaas (${ambienteAsaas(e)}): PIX emergencial conferido com o lote`,
  "validacao_saque.recusada": (e) =>
    `Saque recusado pela validação do Asaas (${ambienteAsaas(e)}): ${texto(e.contexto?.motivo, "motivo não informado").toLowerCase()}`,
  "lote_repasse.transferencia_pendente_autorizacao": (e) =>
    `Transferência do lote de repasse aguardando autorização no Asaas há mais de ${numero(e.contexto?.minutos) ?? 30} minutos — verifique a ação crítica no painel do Asaas`,
  "compra_credito.status_alterado": (e) => {
    const a = alt(e, "status");
    const c = e.contexto ?? {};
    if (a && (a.para === "pago" || a.para === "aprovado")) {
      const itens: any[] = Array.isArray(c.itens) ? c.itens.filter((i: any) => i && typeof i === "object") : [];
      const partes = itens.map((i) => {
        const q = numero(i.quantidade) ?? 0;
        const tv = i.tipo_veiculo ? ` ${valor("tipo_veiculo", i.tipo_veiculo)}` : "";
        return `${plural(q, "crédito", "créditos")}${tv}`;
      });
      const quem = texto(c.usuario_nome, "Um usuário");
      const oQue = partes.length ? partes.join(" e ") : "créditos";
      const vt = numero(c.valor_total);
      const val = vt !== null ? ` (${brl(vt)})` : "";
      return `${quem} comprou ${oQue}${val} — pagamento confirmado`;
    }
    return a ? `Compra de crédito de ${texto(e.contexto?.usuario_nome, "usuário")}: status ${paraDe(a)}` : null;
  },
  "movimento_credito.criado": (e) => {
    const c = e.contexto ?? {};
    const q = numero(c.quantidade) ?? 0;
    const tv = c.tipo_veiculo ? ` ${valor("tipo_veiculo", c.tipo_veiculo)}` : "";
    const ln = texto(typeof c.laudo_numero === "number" ? String(c.laudo_numero) : c.laudo_numero, "");
    const laudo = ln ? ` pelo Laudo #${ln}` : "";
    const verbo: Record<string, [string, string]> = {
      debito: ["debitado", "debitados"], compra: ["creditado (compra)", "creditados (compra)"],
      reserva: ["reservado", "reservados"], liberacao: ["liberado", "liberados"],
      estorno_credito: ["estornado", "estornados"], estorno_dinheiro: ["estornado em dinheiro", "estornados em dinheiro"],
      gratuito: ["creditado (gratuito)", "creditados (gratuito)"],
    };
    const [sing, plu] = verbo[String(c.tipo)] ?? ["movimentado", "movimentados"];
    return `${plural(q, "crédito", "créditos")}${tv} ${q === 1 ? sing : plu}${laudo}`;
  },
};

export function frase(e: EventoAuditoria): string {
  const especial = ESPECIAIS[e.tipo]?.(e);
  if (especial) return especial;

  const quem = ator(e);
  const rot = rotulo(e);
  const ent = ENTIDADES[e.entidade] ?? humanizar(e.entidade);
  const alvo = rot.toLowerCase().startsWith(ent.toLowerCase()) ? rot : `${ent} "${rot}"`;
  const sufixo = e.tipo.split(".")[1] ?? "";

  if (sufixo === "criado") return `${quem} criou ${alvo}`;
  if (sufixo === "excluido") return `${quem} excluiu ${alvo}`;

  if (e.alteracoes?.length) {
    const partes = e.alteracoes.map((a) =>
      a.de === OCULTO || a.para === OCULTO ? `${nomeCampo(a.campo)} alterado (valor não registrado)` : `${nomeCampo(a.campo)} ${paraDe(a)}`,
    );
    return `${quem} alterou ${alvo}: ${partes.join("; ")}`;
  }
  return `${quem} alterou ${alvo}`;
}
